<template>
	<div class="support-calculator-tab">
		<support-calculator
			:province="provinceSnapshot"
			:payor-name-prop="payorNameSnapshot"
			:recipient-name-prop="recipientNameSnapshot"
			:cohabitation-date-prop="cohabProp"
			:separation-date-prop="sepProp"
			:payor-d-o-b-prop="payorDOBSnapshot"
			:recipient-d-o-b-prop="recipientDOBSnapshot"
			:children-prop="childrenSnapshot"
			:initial-state="initialStateSnapshot"
			:matter-context="matterContext"
			:can-record-prop="canRecord"
			:recorded-prop="recorded"
			@update-state="onUpdateState"
			@apply="onApply"
		/>
	</div>
</template>

<script>
// Thin host wrapper around the lawyer-supplied support calculator. It maps the
// matter's party/relationship data onto the calculator's prop contract (see the
// calculator's INTEGRATION.md), persists the calculator's working state on the
// agreement so it survives a reload, and records a chosen scenario into the
// builder's Child Support, Special Expenses, Spousal Support and party income
// entries (the calculator's `apply` event, INTEGRATION.md §6 and §7).
//
// Persistence goes through the Agreement page's working copy (`agreementData`),
// exactly as the builder sections do: mutations are picked up by the page's
// debounced whole-document save. The earlier partial-update path
// (updateAgreementFields + refetch) was dropped, as it was for the pension
// calculator (PR #4060): its refetch rebuilt the working copy and could race
// with the page's own save now that this tab also edits builder entries.
//
// ⚠ SupportCalculator.vue is NO LONGER a verbatim copy. It carries deliberate
// Divii-only deltas on top of the upstream drop (currently source commit
// 0cebce2 of LegalAppVic/support-calculator, branch vue2-port):
//   1. the builder→calculator prop-sync engine (runPropSync /
//      computePropSyncPlan / syncConflicts — see NOTE(sync-reconciliation))
//   2. scrollPageToTop scroll-reset on viewMode/step changes
//   3. the restructure empty-state
//   4. "Federal Tables" wording
//   5. swapParties() also swapping payor/recipient DOBs
//   6. TAX_YEAR_OPTIONS narrowed to [2026, 2025]
//   7. upstream's dev-only window.__diviiAudit block deleted (its Vite-only
//      env guard cannot be parsed by webpack 4 — hard build error here)
//   8. created() validates the restored workspaceTab against what the current
//      role can RENDER (search "DIVII FIX") — without it, a state saved by a
//      level-2 session reopens blank for everyone else
//   9. accessProfile() honours matterContext.readOnly → 'view', so the User
//      Role page's support-calculator Edit permission can make it read-only
//   9. "Record in agreement" (search "DIVII PATCH"): applyToAgreementEnabled is
//      on; the apply payload gains payorIsPartner1, netPayerSlot, the spousal
//      support payor slot, the dialog's term choice and the chosen lump-sum
//      figure; a child-support-only Record button covers matters with no SSAG
//      claim; canRecordProp / recordedProp let this host gate the buttons and
//      own the "In agreement" badge.
// NEVER overwrite the file with an upstream copy: do a 3-way merge with the
// last synced upstream commit as the base, or all of the above is silently
// reverted. See the plan/ADR for the exact procedure.
import { auth } from '../../firebase';
import { hasEditPermission } from '../../mixins/roleLevelPermissions';
import { pureFilters } from '@/lib/agreement/filters';
import dialogs from '@/components/ParentingSchedule/dialogs.js';
import toast from '@/mixins/toast';
import SupportCalculator from './SupportCalculator.vue';

// Roles the calculator renders entirely read-only (Separating Party level 3/4).
// They never legitimately write, but the calculator's created() assigns
// viewMode/workspaceTab for read-only profiles, which trips the viewMode watcher
// -> emitStateUpdate -> onUpdateState. On a matter with no saved state there is
// no _isHydrating guard to swallow it, so a view-only user would otherwise
// create the first supportCalculatorState. Level-2 and sharing users are
// deliberately NOT here — they legitimately enter their own income and budget.
const VIEW_ONLY_ROLES = ['client-level-3', 'client-level-4'];

// Full province/territory name (as stored on the agreement) → the
// jurisdiction codes the calculator understands.
const PROVINCE_CODES = {
	'british columbia': 'BC',
	alberta: 'AB',
	saskatchewan: 'SK',
	manitoba: 'MB',
	ontario: 'ON',
	quebec: 'QC',
	'new brunswick': 'NB',
	'nova scotia': 'NS',
	'prince edward island': 'PE',
	'newfoundland and labrador': 'NL',
	newfoundland: 'NL',
	yukon: 'YT',
	'northwest territories': 'NT',
	nunavut: 'NU',
};

function provinceCode(name) {
	if (!name) return null;
	return PROVINCE_CODES[String(name).trim().toLowerCase()] || null;
}

// Stored dates are ISO timestamps ("1981-02-02T08:00:00.000Z"); the calculator
// wants "yyyy-mm-dd". We must derive the LOCAL calendar date, not slice the UTC
// portion: the builder saves dates as a locally-parsed date -> toISOString(), and
// displays them back in local time. Slicing the UTC string shifts the day by one
// for timezones ahead of/behind UTC (e.g. IST turns 1995-01-01 into 1994-12-31).
function toIsoDate(value) {
	if (!value) return null;
	const s = String(value);
	// Already a plain calendar date (no time component) — pass through untouched.
	if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
	const d = new Date(s);
	if (isNaN(d.getTime())) return s.length >= 10 ? s.slice(0, 10) : null;
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
}

// Display rule: preferred name when set, else "first last". The agreement text
// refers to parties/children by preferred name throughout (see preferredName()
// in @/lib/agreement/filters.js — not reused here because it lacks the
// last-name fallback), so the calculator should match.
function partnerName(partner) {
	if (!partner || !partner.name) return null;
	const preferred = String(partner.name.preferred || '').trim();
	if (preferred) return preferred;
	const name = [partner.name.first, partner.name.last].filter(Boolean).join(' ').trim();
	return name || null;
}

// The name the builder stores for a child in its dependant credit answers
// (pureFilters.preferredName: preferred name, else first name).
function builderChildName(child) {
	if (!child || !child.name) return '';
	return String(child.name.preferred || child.name.first || '').trim();
}


// The builder's Eligible Dependant Credit answer for a child, as the partner
// index that claims it ('0' | '1'), or null. Only a Shared arrangement asks:
// one child under 18 -> childSupport.taxClaimFirstParty; more than one ->
// partner1TaxClaim / partner2TaxClaim (child names, with the child ids stored
// alongside since this feature). Matches by id first, then by stored name.
function builderClaimFor(agreementData, child, under18) {
	const ad = agreementData || {};
	const cs = ad.childSupport || {};
	if (!ad.parenting || ad.parenting.arrangement !== 'Shared') return null;
	if (under18.length === 1) {
		if (under18[0] !== child) return null;
		const first = cs.taxClaimFirstParty;
		return first === '0' || first === 0 ? '0' : first === '1' || first === 1 ? '1' : null;
	}
	const matches = (name, id) =>
		(id !== undefined && id !== null && child.id !== undefined && child.id !== null && String(id) === String(child.id)) ||
		(!isBlank(name) && builderChildName(child) === String(name).trim());
	if (matches(cs.partner1TaxClaim, cs.partner1TaxClaimChildId)) return '0';
	if (matches(cs.partner2TaxClaim, cs.partner2TaxClaimChildId)) return '1';
	return null;
}

function sectionEnabled(agreementData, section) {
	const sections = agreementData && agreementData.agreement && agreementData.agreement.sections;
	return Array.isArray(sections) && sections.includes(section);
}

function isBlank(v) {
	return v === undefined || v === null || String(v).trim() === '';
}

// Builder fields hold numbers or numeric strings depending on which input wrote
// them; compare numerically when both sides parse, otherwise as text.
function sameValue(a, b) {
	if (isBlank(a) && isBlank(b)) return true;
	if (isBlank(a) || isBlank(b)) return false;
	const na = Number(a);
	const nb = Number(b);
	if (!isNaN(na) && !isNaN(nb)) return na === nb;
	return String(a) === String(b);
}

function round2(n) {
	return Math.round(Number(n || 0) * 100) / 100;
}

function round1(n) {
	return Math.round(Number(n || 0) * 10) / 10;
}

function formatMoney(n) {
	return Number(n || 0).toLocaleString('en-CA', {
		style: 'currency',
		currency: 'CAD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

const PAYMENT_TYPE_LABELS = {
	periodic: 'Paid periodically',
	'lump-sum': 'Paid in lump sum',
	waiver: 'No spousal support payable, waiving all future support',
	'no-spousal-no-waiver': 'No spousal support currently payable, right not waived',
};

export default {
	name: 'SupportCalculatorTab',
	components: { SupportCalculator },
	mixins: [dialogs, toast],
	props: {
		agreementData: { type: Object, required: true },
	},
	computed: {
		// Relationship dates stay live (reactive), not snapshotted, so the
		// calculator always reflects the builder's current values. The builder
		// owns these dates.
		cohabProp() {
			const r = (this.agreementData && this.agreementData.relationship) || {};
			return toIsoDate(r.cohabitationDate || r.marriageDate || '');
		},
		sepProp() {
			const r = (this.agreementData && this.agreementData.relationship) || {};
			return toIsoDate(r.separationDate || '');
		},
		// The signed-in user's role ON THIS AGREEMENT — not their global role.
		// Resolved by allPermissionsAvailable() (mixins/roleLevelPermissions.js),
		// which matches the auth email against the agreement's users/permissions
		// doc. The calculator maps the string through its own ROLE_PROFILES table
		// to one of four access profiles.
		// Deliberately NOT store.state.currentRoleLevel: that is the GLOBAL role
		// from customers/{uid}.roleLevel, and the same person can hold different
		// levels on different agreements.
		roleOnAgreement() {
			return this.$store.state.currentAgreementRoleLevelName || null;
		},
		// Stable per-ACCOUNT id for party self-identification. The calculator's
		// party picker stores a claim keyed by this, so the same person keeps
		// their party across devices and sessions.
		// This MUST be passed: with no userId the calculator silently falls back
		// to a per-browser localStorage token (currentUserKey), and the claim
		// then only holds on the device that made it.
		// NOTE: we no longer try to work out the party ourselves. The previous
		// approach matched the auth email against agreement.partner{1,2}.mail,
		// but that is an optional free-text contact field — filled on 1 of 10
		// sampled matters, and not necessarily the address the person signs in
		// with. Upstream now fails closed (rolePartyId returns null rather than
		// guessing party2) and asks the user directly, so we deliberately omit
		// userPartySlot and let the in-tool picker decide.
		userId() {
			const uid = auth.currentUser && auth.currentUser.uid;
			return uid ? String(uid) : null;
		},
		// Display name for the signed-in user. Only used to label who is signed
		// in and to attribute self-reported entries — it never affects access.
		userName() {
			const u = auth.currentUser || {};
			const store = this.$store.state.user || {};
			const name = String(u.displayName || store.displayName || '').trim();
			return name || String(u.email || store.email || '').trim() || null;
		},
		// Role context only. Every identity/prefill field is deliberately absent:
		// the calculator's fromCtx()/pick() give matterContext precedence OVER
		// the flat props, so duplicating the prefill here would shadow it and
		// disturb the builder→calculator sync engine for no gain. Absent keys
		// fall through to the props above.
		// Passed unconditionally — the calculator grants FULL access when `role`
		// is missing, so a conditional skip would silently un-restrict a
		// view-only user. A computed (not a data() snapshot) keeps these live on
		// every open, as the integration doc requires.
		readOnly() {
			return !hasEditPermission('support-calculator');
		},
		matterContext() {
			return { role: this.roleOnAgreement, readOnly: this.readOnly, userId: this.userId, userName: this.userName };
		},
		// Recording writes into three builder areas; the buttons are offered when
		// the user may edit at least one of them. Sections they cannot edit are
		// skipped (and named) when recording.
		canRecord() {
			if (VIEW_ONLY_ROLES.includes(this.roleOnAgreement)) return false;
			return (
				hasEditPermission('section-child-support') ||
				hasEditPermission('section-spousal-support') ||
				hasEditPermission('section-basics')
			);
		},
		// What was last recorded: { scenarioId, scenarioLabel, mode, at, fields }.
		// Passed as null (never undefined) so the calculator knows this host owns
		// the "In agreement" badge.
		recorded() {
			return (this.agreementData && this.agreementData.supportCalculatorRecorded) || null;
		},
		// Recording writes into three builder areas; the buttons are offered when
		// the user may edit at least one of them. Sections they cannot edit are
		// skipped (and named) when recording.
		canRecord() {
			if (VIEW_ONLY_ROLES.includes(this.roleOnAgreement)) return false;
			return (
				hasEditPermission('section-child-support') ||
				hasEditPermission('section-spousal-support') ||
				hasEditPermission('section-basics')
			);
		},
		// What was last recorded: { scenarioId, scenarioLabel, mode, at, fields }.
		// Passed as null (never undefined) so the calculator knows this host owns
		// the "In agreement" badge.
		recorded() {
			return (this.agreementData && this.agreementData.supportCalculatorRecorded) || null;
		},
	},
	data() {
		// Snapshot the party inputs ONCE at creation so a save mid-edit cannot
		// flow back in as new props and reset the calculator. The tab is rendered
		// behind a v-if, so it is recreated (and re-snapshotted from fresh matter
		// data) each time it is opened.
		const ad = this.agreementData || {};
		// "Province of agreement" from Matter Details — not the partners'
		// address province — decides the support jurisdiction.
		const seedProvince =
			(ad.agreement && ad.agreement.updatedProvinceOfAgreement) ||
			(ad.agreement && ad.agreement.provinceOfAgreement) ||
			ad.provinceOfAgreement ||
			'';
		// Default partner1 as payor / partner2 as recipient; the calculator lets
		// the lawyer swap them and persists that choice in its own state.
		// Under 18 at any time in the agreement year, as the builder's own
		// dependant credit question counts them.
		const under18 = pureFilters.childrenUnder18(ad.children || [], ad.agreement && ad.agreement.year);
		const children = (ad.children || [])
			.map((child) => ({
				// Stable builder id (ChildInformation generates/backfills it) —
				// lets the calculator's sync match children exactly across
				// renames and DOB edits. Null for children saved before ids.
				id: child.id || null,
				name: partnerName(child) || '',
				dob: toIsoDate(child.birthdate),
				// The builder's dependant credit answer, so the calculator can
				// start from it (its own explicit choice wins once made).
				edcClaimedByPartner: builderClaimFor(ad, child, under18),
			}))
			.filter((c) => c.name || c.dob);
		return {
			provinceSnapshot: provinceCode(seedProvince),
			payorNameSnapshot: partnerName(ad.partner1),
			recipientNameSnapshot: partnerName(ad.partner2),
			payorDOBSnapshot: toIsoDate(ad.partner1 && ad.partner1.birthdate),
			recipientDOBSnapshot: toIsoDate(ad.partner2 && ad.partner2.birthdate),
			childrenSnapshot: children,
			initialStateSnapshot: ad.supportCalculatorState || null,
		};
	},
	methods: {
		partnerLabel(key) {
			return partnerName(this.agreementData[key]) || (key === 'partner1' ? 'Party 1' : 'Party 2');
		},
		onUpdateState(state) {
			// UI gating is advisory; this is the one place every calculator state
			// write funnels through, so view-only roles are stopped here.
			if (VIEW_ONLY_ROLES.includes(this.roleOnAgreement) || this.readOnly) return;
			// The calculator emits on load with the restored state; skip no-op
			// writes so the page's save is not triggered for nothing.
			const json = JSON.stringify(state);
			if (json === JSON.stringify(this.agreementData.supportCalculatorState || null)) return;
			this.$set(this.agreementData, 'supportCalculatorState', JSON.parse(json));
		},
		// Work out every builder field the payload would change, without writing.
		// Returns { changes, skipped, warnings, blocked }:
		//   changes  [{ container, key, to, from, label, kind }]; kind 'silent' marks a
		//            companion write (a child id) that is not listed (container is a top-level
		//            agreement key: childSupport, childSupportExpenses, spousalSupport,
		//            parenting, partner1, partner2)
		//   skipped  reasons a whole area was left alone (section off, no permission)
		//   warnings things the lawyer should double-check but that do not block
		//   blocked  a message when recording cannot proceed at all
		planRecord(payload) {
			const ad = this.agreementData;
			const changes = [];
			const skipped = [];
			const warnings = [];
			const meta = payload.meta || {};
			const csOnly = meta.scenarioId === 'child-support-only';
			// Slot A (the calculator's payor) is partner1 unless the lawyer swapped
			// the parties; older payloads without the flag are treated as unswapped.
			const payorIsPartner1 = meta.payorIsPartner1 !== false;
			const slotKey = (slot) => ((slot === 'payor') === payorIsPartner1 ? 'partner1' : 'partner2');
			const slotIndex = (slot) => (slotKey(slot) === 'partner1' ? '0' : '1');
			const add = (container, key, to, label, kind) => {
				const from = ad[container] ? ad[container][key] : undefined;
				if (sameValue(from, to)) return;
				changes.push({ container, key, to, from, label, kind });
			};

			// ── Child support and Section 7 ─────────────────────────────────
			const cs = payload.childSupport;
			if (cs) {
				if (!sectionEnabled(ad, 'Child Support')) {
					skipped.push('Child Support (the section is not part of this agreement)');
				} else if (!hasEditPermission('section-child-support')) {
					skipped.push('Child Support (your role cannot edit that section)');
				} else {
					const parenting = ad.parenting || {};
					const arrangement = parenting.arrangement;
					// The calculator says whether a set-off applies ('shared': any shared
					// child, or split custody) or every child lives primarily with the same
					// parent ('primary'). Older payloads lack the field; fall back to the
					// builder's own arrangement.
					const calcShared = cs.arrangement === 'shared';
					const useShared = calcShared || arrangement === 'Shared';
					if (!useShared && !arrangement) {
						return {
							changes,
							skipped,
							warnings,
							blocked:
								'Set the Parenting Arrangement in the builder first (Parenting or Child Support section), then record again. ' +
								'Divii needs it to know whether one child support amount or a shared set-off applies.',
						};
					}
					if (useShared) {
						// Rebecca (2026-09-19): a hybrid pattern (one child shared, another
						// primarily with one parent) is recorded with the builder's Shared
						// arrangement, using each parent's obligation and the set-off.
						if (arrangement !== 'Shared') {
							const custody = cs.custody || {};
							const parts = [];
							if (custody.shared) parts.push(`${custody.shared} shared`);
							if (custody.withPayor) parts.push(`${custody.withPayor} primarily with ${this.partnerLabel(slotKey('payor'))}`);
							if (custody.withRecipient) parts.push(`${custody.withRecipient} primarily with ${this.partnerLabel(slotKey('recipient'))}`);
							warnings.push(
								`The calculator's children are ${parts.join(', ') || 'in a shared or split arrangement'}, so child support is recorded ` +
									'as a Shared parenting arrangement with a set-off. Check the Parenting and Child Support sections afterwards.'
							);
							add('parenting', 'arrangement', 'Shared', 'Parenting arrangement', 'text');
							if (!isBlank((ad.childSupport || {}).payment)) add('childSupport', 'payment', undefined, 'Child support payable (single amount)', 'money');
						}
						const payorOwes = cs.payorOwes !== undefined ? cs.payorOwes : cs.payorTable;
						const recipientOwes = cs.recipientOwes !== undefined ? cs.recipientOwes : cs.recipientTable;
						const p1Owes = payorIsPartner1 ? payorOwes : recipientOwes;
						const p2Owes = payorIsPartner1 ? recipientOwes : payorOwes;
						add('childSupport', 'partner1Amount', round2(p1Owes), `${this.partnerLabel('partner1')} pays child support of`, 'money');
						add('childSupport', 'partner2Amount', round2(p2Owes), `${this.partnerLabel('partner2')} pays child support of`, 'money');
						if (cs.netPayerSlot) {
							add('childSupport', 'setOffParty', slotIndex(cs.netPayerSlot), 'Set-off paid by', 'party');
						}
						// Same formatting the Child Support section's own watcher uses.
						add('childSupport', 'setOffAmount', Number(cs.net || 0).toFixed(2), 'Set-off amount', 'money');
					} else {
						const primary = parenting.primaryParent;
						const nonPrimaryKey = primary === '0' || primary === 0 ? 'partner2' : primary === '1' || primary === 1 ? 'partner1' : null;
						if (!nonPrimaryKey) {
							return {
								changes,
								skipped,
								warnings,
								blocked:
									'Choose the primary parent in the builder first (Parenting or Child Support section), then record again. ' +
									'Child support is payable by the other parent.',
							};
						}
						const netPayerKey = cs.netPayerSlot ? slotKey(cs.netPayerSlot) : null;
						if (netPayerKey && netPayerKey !== nonPrimaryKey) {
							warnings.push(
								`The calculator has ${this.partnerLabel(netPayerKey)} paying net child support, but the builder's primary ` +
									`parenting arrangement has ${this.partnerLabel(nonPrimaryKey)} paying. Check the parenting arrangement ` +
									'and where each child lives in the calculator.'
							);
						}
						add('childSupport', 'payment', round2(cs.net), `Child support payable by ${this.partnerLabel(nonPrimaryKey)}`, 'money');
					}
					// ── Eligible Dependant Credit ────────────────────────────
					// The calculator resolves who claims each eligible child. The full
					// list is stored for the agreement text; the builder's own questions
					// (one shared child: who claims first; several: which child each
					// parent claims) are filled from it for a Shared arrangement.
					const claims = Array.isArray(cs.dependantClaims) ? cs.dependantClaims : null;
					if (claims) {
						const toParty = (slot) =>
							slot === 'payor' || slot === 'recipient' ? slotIndex(slot) : slot === 'shared' ? 'shared' : 'none';
						const mapped = claims.map((c) => ({
							childId: c.id !== undefined && c.id !== null ? c.id : null,
							name: c.name || '',
							residence: toParty(c.residence),
							claimedBy: toParty(c.claimedBy),
						}));
						const current = (ad.childSupport && ad.childSupport.dependantClaims) || null;
						if (JSON.stringify(current) !== JSON.stringify(mapped)) {
							changes.push({ container: 'childSupport', key: 'dependantClaims', to: mapped, from: current, label: 'Eligible dependant credit', kind: 'claims' });
						}
						if (useShared) {
							if (mapped.length > 1) {
								[['0', 'partner1'], ['1', 'partner2']].forEach(([idx, key]) => {
									const claimed = mapped.find((c) => c.claimedBy === idx);
									add('childSupport', `${key}TaxClaim`, claimed ? claimed.name : undefined, `${this.partnerLabel(key)} claims as a dependant`, 'text');
									add('childSupport', `${key}TaxClaimChildId`, claimed ? claimed.childId : undefined, `${this.partnerLabel(key)} claims as a dependant (child id)`, 'silent');
								});
							} else if (mapped.length === 1 && (mapped[0].claimedBy === '0' || mapped[0].claimedBy === '1')) {
								add('childSupport', 'taxClaimFirstParty', mapped[0].claimedBy, 'Eligible dependant credit claimed first by', 'party');
							}
						}
					}
					const se = payload.specialExpenses;
					if (se) {
						const p1Pct = payorIsPartner1 ? se.payorPct : se.recipientPct;
						const p2Pct = payorIsPartner1 ? se.recipientPct : se.payorPct;
						add('childSupportExpenses', 'partner1Proportion', round1(p1Pct), `${this.partnerLabel('partner1')}'s share of special expenses`, 'percent');
						add('childSupportExpenses', 'partner2Proportion', round1(p2Pct), `${this.partnerLabel('partner2')}'s share of special expenses`, 'percent');
					}
				}
			}

			// ── Spousal support ────────────────────────────────────────────
			const ss = payload.spousalSupport;
			if (ss && !csOnly) {
				if (!ss.applicable) {
					skipped.push('Spousal Support (the calculator has no spousal support claim; existing entries left as they are)');
				} else if (!sectionEnabled(ad, 'Spousal Support')) {
					skipped.push('Spousal Support (the section is not part of this agreement)');
				} else if (!hasEditPermission('section-spousal-support')) {
					skipped.push('Spousal Support (your role cannot edit that section)');
				} else {
					const payorKey = slotKey(ss.payor === 'recipient' ? 'recipient' : 'payor');
					add('spousalSupport', 'payor', payorKey === 'partner1' ? '0' : '1', 'Spousal support paid by', 'party');
					if (ss.mode === 'lumpSum') {
						const lump = ss.lumpSum || {};
						const amount = lump.chosen !== undefined && lump.chosen !== null ? lump.chosen : lump.midpoint;
						add('spousalSupport', 'paymentType', 'lump-sum', 'Spousal support is', 'paymentType');
						add('spousalSupport', 'lumpSumPayment', round2(amount), 'Lump sum spousal support', 'money');
					} else {
						const periodic = ss.periodic || {};
						add('spousalSupport', 'paymentType', 'periodic', 'Spousal support is', 'paymentType');
						add('spousalSupport', 'periodicPayment', round2(periodic.monthlyAmount), 'Monthly spousal support', 'money');
						add('spousalSupport', 'endPoint', periodic.indefinite ? 'Indefinitely' : 'specified end point', 'Spousal support is paid', 'text');
						if (!periodic.indefinite && Number(periodic.durationYears) > 0) {
							warnings.push(
								`Spousal support runs for ${periodic.durationYears} years in the calculator. Divii records "specified end point"; ` +
									'set the end date in the Spousal Support section.'
							);
						}
					}
				}
			}

			// ── Party income (Basics) ──────────────────────────────────────
			if (payload.payor && payload.recipient) {
				if (!hasEditPermission('section-basics')) {
					skipped.push('Party income (your role cannot edit the Basics section)');
				} else {
					[
						['payor', payload.payor],
						['recipient', payload.recipient],
					].forEach(([slot, party]) => {
						const key = slotKey(slot);
						if (!Number(party.guidelinesIncome)) {
							skipped.push(`${this.partnerLabel(key)}'s income (none entered in the calculator)`);
							return;
						}
						add(key, 'income', round2(party.guidelinesIncome), `${this.partnerLabel(key)}'s income`, 'money');
					});
				}
			}

			return { changes, skipped, warnings, blocked: null };
		},
		describeValue(change, value) {
			if (isBlank(value)) return 'blank';
			switch (change.kind) {
				case 'money':
					return formatMoney(value);
				case 'percent':
					return `${value}%`;
				case 'party':
					return this.partnerLabel(String(value) === '0' ? 'partner1' : 'partner2');
				case 'paymentType':
					return PAYMENT_TYPE_LABELS[value] || String(value);
				case 'claims':
					return (Array.isArray(value) ? value : [])
						.map((c) =>
							c.claimedBy === '0' || c.claimedBy === '1'
								? `${this.partnerLabel(c.claimedBy === '0' ? 'partner1' : 'partner2')} claims ${c.name || 'a child'}`
								: `${c.name || 'a child'} not claimed`
						)
						.join('; ');
				default:
					return String(value);
			}
		},
		async onApply(payload) {
			if (!this.canRecord || !payload) return;
			const label = (payload.meta && payload.meta.scenarioLabel) || 'scenario';
			const plan = this.planRecord(payload);
			if (plan.blocked) {
				this.errorMessage('Cannot record yet', plan.blocked);
				return;
			}
			if (!plan.changes.filter((c) => c.kind !== 'silent').length) {
				this.infoMessage(
					'Nothing to record',
					`The builder already matches "${label}".` + (plan.skipped.length ? ` Not written: ${plan.skipped.join('; ')}.` : '')
				);
				return;
			}
			const lines = [`Recording "${label}" will update the agreement builder:`];
			const visible = plan.changes.filter((c) => c.kind !== 'silent');
			visible.forEach((c) => {
				lines.push(`• ${c.label}: ${this.describeValue(c, c.from)} → ${this.describeValue(c, c.to)}`);
			});
			plan.warnings.forEach((w) => lines.push(`Warning: ${w}`));
			if (plan.skipped.length) lines.push(`Not written: ${plan.skipped.join('; ')}.`);
			lines.push('You can still edit every recorded value in the builder.');
			const ok = await this.$confirmDialog(lines.join('\n'), {
				title: 'Record in agreement?',
				okTitle: 'Record',
				cancelTitle: 'Cancel',
			});
			if (!ok) return;
			this.writeChanges(plan.changes);
			const ss = payload.spousalSupport || {};
			this.$set(this.agreementData, 'supportCalculatorRecorded', {
				scenarioId: payload.meta.scenarioId,
				scenarioLabel: label,
				mode: ss.applicable ? ss.mode || 'periodic' : null,
				at: new Date().toISOString(),
				fields: visible.map((c) => c.label),
			});
			const count = visible.length;
			this.successMessage(
				'Recorded in agreement',
				`${count} builder ${count === 1 ? 'entry' : 'entries'} updated from "${label}".` +
					(plan.skipped.length ? ` Not written: ${plan.skipped.join('; ')}.` : '')
			);
		},
		writeChanges(changes) {
			const ad = this.agreementData;
			changes.forEach((c) => {
				if (!ad[c.container]) this.$set(ad, c.container, {});
				this.$set(ad[c.container], c.key, c.to);
			});
		},
	},
};
</script>

<style scoped>
.support-calculator-tab {
	width: 100%;
}
</style>
